import { useState, useEffect } from 'react';
import { FileText, ExternalLink, FileSpreadsheet, FileType, Printer } from 'lucide-react';
import { mediaKind, fileName } from '../lib/media';
import { printUrl } from '../lib/print';
import PdfViewer from './PdfViewer';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function absUrl(src) {
  if (!src) return src;
  return src.startsWith('http') ? src : `${BACKEND}${src}`;
}

// Reusable preview tile for non-image/non-video gallery items (PDFs,
// Office docs, CAD). PDFs render the first page inline via pdf.js so
// they look like real photos in the gallery. Office/CAD/other files
// fall through to the colour-coded icon card. Click opens the file in
// a new tab.
//
// Props:
//   src       - file URL (relative /api/uploads/... or absolute)
//   title     - optional caption override (defaults to filename)
//   compact   - true → small icon + caption (for carousel thumbs)
export default function MediaTile({ src, title, compact = false }) {
  const kind = mediaKind(src);
  const href = absUrl(src);
  const caption = title || fileName(src) || 'File';
  const [pdfFailed, setPdfFailed] = useState(false);

  // Reset failure state when src changes so a previously-failed URL
  // can retry if it's been replaced.
  useEffect(() => { setPdfFailed(false); }, [src]);

  // PDF — render an inline page-by-page viewer so multi-page docs can be
  // flipped through without leaving the gallery. Compact tiles (carousel
  // thumb strip) suppress arrows + counter so the strip stays clean.
  if (kind === 'pdf' && !pdfFailed) {
    return (
      <a
        data-testid="media-tile-pdf"
        href={href}
        target="_blank"
        rel="noreferrer"
        className="w-full h-full block relative overflow-hidden rounded-lg group"
        title={`Open ${caption} in a new tab`}
        onClick={(e) => e.stopPropagation()}
      >
        <PdfViewer
          url={href}
          onFail={() => setPdfFailed(true)}
          showArrows={compact ? 'never' : 'hover'}
          showCounter={compact ? 'never' : 'auto'}
        />
        {/* Print button — hidden in compact mode (thumb strip stays clean) */}
        {!compact && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); printUrl(href, caption); }}
            data-testid="media-tile-print"
            title={`Print ${caption}`}
            className="absolute bottom-1 right-1 inline-flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
            style={{ width: 28, height: 28, backgroundColor: 'rgba(0,0,0,0.65)', color: 'white' }}
          >
            <Printer className="w-3.5 h-3.5" />
          </button>
        )}
        {/* Caption appears on hover so the thumbnail still reads as a picture by default */}
        {!compact && (
          <span
            className="absolute bottom-0 left-0 right-0 px-2 py-1 text-[9px] font-semibold text-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
            style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.75), transparent)', fontFamily: 'Manrope' }}
          >
            {caption}
          </span>
        )}
      </a>
    );
  }

  // Fallback (or non-PDF) — colour-coded icon card.
  let Icon = FileText;
  let label = 'File';
  let bg = '#FAF8F2';
  let accent = '#1F4FBE';
  if (kind === 'pdf') { Icon = FileText; label = 'PDF'; bg = '#FDECEC'; accent = '#DC2626'; }
  else if (kind === 'doc') { Icon = FileSpreadsheet; label = 'Doc'; bg = '#EAF2FF'; accent = '#1F4FBE'; }
  else if (kind === 'cad') { Icon = FileType; label = 'CAD'; bg = '#F5F0E1'; accent = '#92400E'; }
  return (
    <div
      className="w-full h-full relative group"
    >
      <a
        data-testid={`media-tile-${kind}`}
        href={href}
        target="_blank"
        rel="noreferrer"
        className="w-full h-full flex flex-col items-center justify-center gap-1.5 rounded-lg p-2 transition hover:-translate-y-0.5"
        style={{ backgroundColor: bg, border: `1px solid ${accent}33`, fontFamily: 'Manrope', color: accent }}
        title={`Open ${caption} in a new tab`}
        onClick={(e) => e.stopPropagation()}
      >
        <Icon className={compact ? 'w-5 h-5' : 'w-8 h-8'} style={{ color: accent }} />
        {!compact && (
          <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: accent }}>
            {label}
          </span>
        )}
        <span
          className={(compact ? 'text-[8px] ' : 'text-[10px] ') + 'font-semibold text-center break-words leading-tight line-clamp-2 px-1'}
          style={{ color: '#1A2E2E', maxWidth: '100%' }}
        >
          {caption}
        </span>
        {!compact && (
          <span className="inline-flex items-center gap-0.5 text-[9px] font-bold mt-0.5" style={{ color: accent }}>
            <ExternalLink className="w-2.5 h-2.5" /> Open
          </span>
        )}
      </a>
      {!compact && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); printUrl(href, caption); }}
          data-testid="media-tile-print"
          title={`Print ${caption}`}
          className="absolute top-1 right-1 inline-flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
          style={{ width: 24, height: 24, backgroundColor: accent, color: 'white' }}
        >
          <Printer className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}
